<script setup lang="ts">
export type Appearance = 'Button' | 'Link'
export type Variants =
  | 'primary'
  | 'secondary'
  | 'secondary-reverse'
  | 'tertiary'
  | 'bold'
  | 'disclaimer'
  | 'quickLinks'
  | 'selector'
  | 'sale'
  | 'text'
  | 'white'
  | 'none'

export type ButtonProps = {
  appearance?: Appearance
  variant?: Variants
  text?: string
  gaOptions?: DataLayerOptional
  logHover?: boolean // @TODO
  logClick?: boolean // @TODO
}

const props = withDefaults(defineProps<ButtonProps>(), {
  appearance: 'Button',
  variant: 'tertiary',
})

function getFallbackAppearance(variant: Variants) {
  return ['text', 'sale', 'bold', 'white', 'disclaimer'].includes(variant) ? 'Link' : 'Button'
}

const variant = props.variant?.replace('-button', '').replace('-link', '').replace('-text', '') as Variants // clean up variant names
const appearance = props.appearance || getFallbackAppearance(variant)
const classPrefix = `${appearance.toLowerCase()}-` // link- or button-
const classes = `${classPrefix}${variant}`
</script>

<template>
  <button ontouchstart.passive="" v-bind="{ ...$attrs, class: classes }">
    <slot>{{ text }}</slot>
  </button>
</template>

<style>
/* Common styles for Button/Link - Different tags but same look & feel  */
.button-primary,
.button-secondary,
.button-secondary-reverse,
.button-tertiary,
.button-selector {
  color: var(--text-primary);
  background: transparent;
  border: 1px solid currentColor;
  cursor: pointer;
  text-underline-offset: 0.15rem;
  outline-offset: 0.2rem;
  width: fit-content;
  display: inline-flex; /* required for scale */
  align-items: center;
  justify-content: center;
  transition: var(--transition-1);
  transition-property: color, background;
  overflow: hidden;
  padding: var(--space-40) var(--space-60);
  line-height: 1;
  min-height: 3rem; /* 48px */
  height: min-content;

  &:active {
    transform: scale(0.98);
  }

  &[disabled] {
    pointer-events: none;
    color: var(--text-disabled);
    cursor: default;
  }
}

.button-primary,
.button-secondary,
.button-secondary-reverse {
  text-decoration: none;
  text-align: center;
  font-weight: bold;
  letter-spacing: var(--letter-spacing-1);
  text-transform: uppercase;
  min-width: 11rem; /* 176px */

  @media (--desktop) {
    max-width: var(--text-max-width);
  }
}

.button-primary {
  background: var(--surface-dark-10);
  color: var(--text-reverse);
  border: 1px solid var(--surface-dark-10);

  &:hover {
    color: var(--text-reverse);
    background: var(--surface-dark-20);
  }

  &[disabled] {
    background: var(--surface-dark-10-t40);
  }
}

.button-secondary {
  color: #010101;

  &:hover {
    color: var(--text-link-hover);
  }

  &[disabled] {
    color: var(--surface-dark-10-t40);
  }
}

.button-secondary-reverse,
.button-secondary-inverted {
  background: transparent;
  color: var(--text-reverse);

  &:hover {
    color: var(--text-link-reverse-hover);
  }

  &[disabled] {
    color: var(--border-neutral-00-t40);
  }
}

.button-tertiary {
  border: none;
  text-transform: uppercase;
  text-decoration: underline;
  padding: 0;
  margin: 0;
  line-height: 1.25; /* For underline to be visible */
  min-height: unset;

  &:hover {
    color: var(--text-link-hover);
  }

  &[disabled] {
    color: var(--text-disabled);
  }
}

.button-selector {
  width: 100%;
  justify-content: flex-start;
  font-weight: 500;
  border-color: var(--border-input);
  padding-inline: var(--space-50);

  &:after {
    content: '';
    background: url('~/assets/sprite/svg/arrow-carousel-right.svg') no-repeat center / var(--space-50) var(--space-50);
    width: var(--space-60);
    height: var(--space-60);
    position: relative;
    margin-left: auto;
  }
}

.button-text,
.link-primary,
.link-secondary,
.link-secondary-reverse,
.link-secondary-inverted,
.link-tertiary {
  text-decoration: underline;
  text-underline-offset: 0.15rem;
  display: inline-flex;
  align-items: center;
  line-height: 120%;
  transition: var(--transition-1);
  transition-property: color;

  &:hover {
    color: var(--text-link-hover);
  }

  &[disabled] {
    color: var(--text-disabled);
    pointer-events: none;
    cursor: default;
  }
}

.link-sale {
  color: var(--text-sale) !important;
  text-decoration-color: inherit;

  &:hover {
    color: inherit;
    text-decoration-color: inherit;
  }
}

.link-bold {
  font-weight: bold;
}

.link-white {
  color: var(--text-reverse);

  &:hover {
    color: var(--text-link-reverse-hover);
  }
}

.link-disclaimer {
  font-size: var(--text-disclaimer);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
